import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';

const AuditLogs = () => {
  const { user } = useAuth();
  const [filterType, setFilterType] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');

  const [logs] = useState([
    {
      id: 1,
      user: user?.name || 'Admin User',
      action: 'Login',
      resource: 'Authentication',
      details: 'Successful login from 192.168.1.100',
      timestamp: new Date(Date.now() - 3600000),
      status: 'success',
      type: 'auth'
    },
    {
      id: 2,
      user: user?.name || 'Admin User',
      action: 'Created Threat',
      resource: 'Threat Detection',
      details: 'Added new threat: Ransomware on Device-001',
      timestamp: new Date(Date.now() - 7200000),
      status: 'success',
      type: 'threat'
    },
    {
      id: 3,
      user: user?.name || 'Admin User',
      action: 'Updated Policy',
      resource: 'Access Control',
      details: 'Modified Zero-Trust Access Policy settings',
      timestamp: new Date(Date.now() - 10800000),
      status: 'success',
      type: 'policy'
    },
    {
      id: 4,
      user: 'john@company.com',
      action: 'Failed Login Attempt',
      resource: 'Authentication',
      details: 'Invalid password from 192.168.1.105',
      timestamp: new Date(Date.now() - 14400000),
      status: 'failed',
      type: 'auth'
    },
    {
      id: 5,
      user: user?.name || 'Admin User',
      action: 'Invited Team Member',
      resource: 'Team Management',
      details: 'Sent invitation to jane@company.com',
      timestamp: new Date(Date.now() - 18000000),
      status: 'success',
      type: 'team'
    },
    {
      id: 6,
      user: user?.name || 'Admin User',
      action: 'Device Registered',
      resource: 'Device Management',
      details: 'New device registered: MacBook-Pro-Office',
      timestamp: new Date(Date.now() - 21600000),
      status: 'success',
      type: 'device'
    },
    {
      id: 7,
      user: user?.name || 'Admin User',
      action: 'Report Generated',
      resource: 'Reports',
      details: 'Generated Security Summary report for last 7 days',
      timestamp: new Date(Date.now() - 25200000),
      status: 'success',
      type: 'report'
    },
    {
      id: 8,
      user: user?.name || 'Admin User',
      action: 'Configuration Changed',
      resource: 'Settings',
      details: 'Updated email notification preferences',
      timestamp: new Date(Date.now() - 28800000),
      status: 'success',
      type: 'settings'
    },
  ]);

  const filteredLogs = logs.filter(log => {
    const matchesType = filterType === 'all' || log.type === filterType;
    const matchesSearch = log.action.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         log.resource.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         log.details.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesType && matchesSearch;
  });

  const getStatusColor = (status) => {
    return status === 'success' 
      ? 'bg-green-500/20 text-green-400 border-green-500/30'
      : 'bg-red-500/20 text-red-400 border-red-500/30';
  };

  const getTypeColor = (type) => {
    const colors = {
      auth: 'bg-blue-500/20 text-blue-400',
      threat: 'bg-red-500/20 text-red-400',
      policy: 'bg-purple-500/20 text-purple-400',
      team: 'bg-cyan-500/20 text-cyan-400',
      device: 'bg-green-500/20 text-green-400',
      report: 'bg-yellow-500/20 text-yellow-400',
      settings: 'bg-gray-500/20 text-gray-400',
    };
    return colors[type] || 'bg-gray-500/20 text-gray-400';
  };

  return (
    <div className="min-h-screen bg-cyber-dark">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-white">Audit Logs</h1>
          <p className="text-gray-400 mt-1">Track all system activities and user actions</p>
        </div>

        <div className="bg-cyber-card border border-gray-800 rounded-lg p-6 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm text-gray-400 mb-2">Filter by Type</label>
              <select
                value={filterType}
                onChange={(e) => setFilterType(e.target.value)}
                className="w-full px-4 py-2 bg-cyber-darker border border-gray-700 rounded-lg text-white focus:border-cyber-cyan focus:outline-none"
              >
                <option value="all">All Activities</option>
                <option value="auth">Authentication</option>
                <option value="threat">Threats</option>
                <option value="policy">Policies</option>
                <option value="team">Team Management</option>
                <option value="device">Devices</option>
                <option value="report">Reports</option>
                <option value="settings">Settings</option>
              </select>
            </div>
            <div>
              <label className="block text-sm text-gray-400 mb-2">Search Logs</label>
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search actions, resources, or details..."
                className="w-full px-4 py-2 bg-cyber-darker border border-gray-700 rounded-lg text-white focus:border-cyber-cyan focus:outline-none"
              />
            </div>
          </div>
        </div>

        <div className="bg-cyber-card border border-gray-800 rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-cyber-darker border-b border-gray-700">
                <tr>
                  <th className="text-left py-4 px-4 text-sm font-medium text-gray-400">Timestamp</th>
                  <th className="text-left py-4 px-4 text-sm font-medium text-gray-400">User</th>
                  <th className="text-left py-4 px-4 text-sm font-medium text-gray-400">Action</th>
                  <th className="text-left py-4 px-4 text-sm font-medium text-gray-400">Resource</th>
                  <th className="text-left py-4 px-4 text-sm font-medium text-gray-400">Details</th>
                  <th className="text-left py-4 px-4 text-sm font-medium text-gray-400">Type</th>
                  <th className="text-left py-4 px-4 text-sm font-medium text-gray-400">Status</th>
                </tr>
              </thead>
              <tbody>
                {filteredLogs.length === 0 ? (
                  <tr>
                    <td colSpan="7" className="text-center py-8 text-gray-400">
                      No audit logs found matching your filters
                    </td>
                  </tr>
                ) : (
                  filteredLogs.map((log) => (
                    <tr key={log.id} className="border-b border-gray-700 hover:bg-cyber-darker transition-colors">
                      <td className="py-4 px-4 text-sm text-gray-300">
                        {log.timestamp.toLocaleString()}
                      </td>
                      <td className="py-4 px-4 text-sm text-white font-medium">
                        {log.user}
                      </td>
                      <td className="py-4 px-4 text-sm text-white">
                        {log.action}
                      </td>
                      <td className="py-4 px-4 text-sm text-gray-300">
                        {log.resource}
                      </td>
                      <td className="py-4 px-4 text-sm text-gray-400">
                        {log.details}
                      </td>
                      <td className="py-4 px-4">
                        <span className={`px-2 py-1 text-xs rounded ${getTypeColor(log.type)}`}>
                          {log.type}
                        </span>
                      </td>
                      <td className="py-4 px-4">
                        <span className={`px-2 py-1 text-xs rounded border ${getStatusColor(log.status)}`}>
                          {log.status}
                        </span>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mt-6">
          <div className="bg-cyber-card border border-gray-800 rounded-lg p-4">
            <h3 className="text-sm text-gray-400 mb-1">Total Activities</h3>
            <p className="text-2xl font-bold text-white">{logs.length}</p>
          </div>
          <div className="bg-cyber-card border border-gray-800 rounded-lg p-4">
            <h3 className="text-sm text-gray-400 mb-1">Successful</h3>
            <p className="text-2xl font-bold text-green-400">
              {logs.filter(l => l.status === 'success').length}
            </p>
          </div>
          <div className="bg-cyber-card border border-gray-800 rounded-lg p-4">
            <h3 className="text-sm text-gray-400 mb-1">Failed</h3>
            <p className="text-2xl font-bold text-red-400">
              {logs.filter(l => l.status === 'failed').length}
            </p>
          </div>
          <div className="bg-cyber-card border border-gray-800 rounded-lg p-4">
            <h3 className="text-sm text-gray-400 mb-1">Today</h3>
            <p className="text-2xl font-bold text-cyan-400">
              {logs.filter(l => new Date(l.timestamp).toDateString() === new Date().toDateString()).length}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AuditLogs;